import { useContext, useEffect } from 'react';
import { JourneyContext, useJourneyManager } from './hooks/useJourney';
import { LayoutContext } from './hooks/useLayoutContext';
import { useAppFlow } from './hooks/useAppFlow';
import ChulakSession from './chulak/ChulakSession';
import { pages, resolveLayoutMode } from './pages';
import LandingPage from './pages/LandingPage';

/**
 * App — Provider shell. Creates the JourneyContext so all descendants
 * (including useAppFlow → useCancelConfirm) can consume it.
 */
function App() {
  const journey = useJourneyManager();

  return (
    <JourneyContext.Provider value={journey}>
      <AppContent />
    </JourneyContext.Provider>
  );
}

/**
 * AppContent — Renders inside JourneyContext.Provider, so hooks that
 * consume the journey (useCancelConfirm, useJourney) work safely.
 *
 * Layout differences are resolved once, up front, by picking a page set from
 * the `pages` map (see src/pages/index.ts). Everything below renders `Page.*`,
 * so the switch only cares about the flow state, not the platform.
 */
function AppContent() {
  const journey = useContext(JourneyContext)!;
  const {
    state,
    isMobile,
    isEmbedded,
    requestedDataGroups,
    requestedDataLoading,
    issuedValueMap,
    layoutData,
    handleCancel,
    dispatch,
    deliveryMode,
    notifyParent,
  } = useAppFlow(journey);

  // Wire the delivery mode and parent notifier into the journey manager.
  useEffect(() => {
    journey.setDeliveryMode(deliveryMode);
  }, [journey, deliveryMode]);

  useEffect(() => {
    journey.setNotifyParent(notifyParent);
  }, [journey, notifyParent]);

  const Page = pages[resolveLayoutMode({ isMobile, isEmbedded })];

  const content = (() => {
    switch (state.type) {
      case 'appInstall':
        return <Page.AppInstall appName={state.appName} />;

      case 'landing':
        return <LandingPage />;

      case 'loading':
        return <Page.Init />;

      case 'error':
        return (
          <Page.Abort
            variant={state.variant}
            onReject={
              isEmbedded || !state.canLeave ? undefined : () => journey.reject()
            }
            onRestart={isEmbedded ? undefined : () => window.location.reload()}
          />
        );

      case 'mobileFlowStart': {
        // Embedded mode never enters this state (treated as desktop in the
        // reducer), so this is always the mobile page.
        const { session } = state;
        return (
          <pages.mobile.FlowStart
            singleApp={session.tokens.length === 1}
            onContinue={() => dispatch({ action: 'MOBILE_CONTINUE' })}
            onCancel={handleCancel}
          />
        );
      }

      case 'appSelect': {
        const { session } = state;
        return (
          <Page.AppPicker
            tokens={session.tokens}
            handlerApps={session.handlerApps}
            recommendedHandlerAppUuids={
              session.context.request.recommendedHandlerAppsUuids ?? []
            }
            onSelect={(token) => dispatch({ action: 'SELECT_TOKEN', token })}
            onCancel={handleCancel}
          />
        );
      }

      case 'issuanceReview': {
        const { session } = state;
        return (
          <Page.IssuanceReview
            client={session.context.request.client}
            requestedDataGroups={requestedDataGroups}
            requestedDataLoading={requestedDataLoading}
            issuedValueMap={issuedValueMap}
            onAccept={() => dispatch({ action: 'REVIEW_ACCEPTED' })}
            onCancel={handleCancel}
            onBack={
              session.tokens.length > 1
                ? () => dispatch({ action: 'BACK_TO_PICKER' })
                : undefined
            }
          />
        );
      }

      case 'chulak': {
        const { session, selectedToken } = state;
        return (
          <ChulakSession
            key={selectedToken.payload.body.handler}
            pages={Page}
            session={session}
            selectedToken={selectedToken}
            requestedDataGroups={requestedDataGroups}
            onBack={
              session.tokens.length > 1 &&
              session.context.request.type !== 'issuance'
                ? () => dispatch({ action: 'BACK_TO_PICKER' })
                : undefined
            }
          />
        );
      }
    }
  })();

  return (
    <LayoutContext.Provider value={layoutData}>
      {content}
    </LayoutContext.Provider>
  );
}

export default App;
